"use client";

import { Checkbox } from "@cmp-src/primitives/checkbox";
import { cn } from "@gettactic/helpers";
import React from "react";
import { classes } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/utils";
import type { FollowUp } from "@/features/workplace-requests/lib/followUpRequests";

interface LinkedWorkflowsChecklistProps {
  followUps: FollowUp[];
  /** Workflow ids the approver has unchecked. */
  skippedWorkflowIds: string[];
  /** Names the request type a follow-up is filed under. */
  typeName: (typeId: string) => string | null;
  onToggle: (workflowId: string) => void;
  /** Freezes the checkboxes while the approval is in flight. */
  isDisabled?: boolean;
}

/**
 * The requests approving this one would file, each with a checkbox that drops
 * it from this approval. It sits inside the approval box rather than behind the
 * Approve click: what approving is about to create is part of the decision, not
 * a confirmation step after it.
 *
 * Skipping is per workflow, so a rule that files two requests offers one
 * checkbox covering both — it creates them together or not at all.
 */
export const LinkedWorkflowsChecklist: React.FC<
  LinkedWorkflowsChecklistProps
> = ({ followUps, skippedWorkflowIds, typeName, onToggle, isDisabled }) => (
  <div className="mt-3 rounded-brand border border-amber-200 bg-white p-3">
    <h4 className={cn(classes.sectionHeading, "text-amber-800")}>
      Linked workflows to create on approve
    </h4>
    <ul className="mt-2 flex flex-col gap-1">
      {followUps.map((followUp) => (
        <li key={followUp.workflowId}>
          <LinkedWorkflowRow
            followUp={followUp}
            isSkipped={skippedWorkflowIds.includes(followUp.workflowId)}
            typeName={typeName}
            onToggle={onToggle}
            isDisabled={isDisabled}
          />
        </li>
      ))}
    </ul>
  </div>
);

interface LinkedWorkflowRowProps {
  followUp: FollowUp;
  isSkipped: boolean;
  typeName: (typeId: string) => string | null;
  onToggle: (workflowId: string) => void;
  isDisabled?: boolean;
}

const LinkedWorkflowRow: React.FC<LinkedWorkflowRowProps> = ({
  followUp,
  isSkipped,
  typeName,
  onToggle,
  isDisabled
}) => {
  // The title a rule gives a request is the useful label; without one the
  // request type's name is all there is to call it by, and repeating it
  // underneath as the type would say the same thing twice.
  const labels = followUp.children.map(
    (child) => child.title || typeName(child.typeId) || "New request"
  );
  const types = followUp.children
    .map((child) => typeName(child.typeId))
    .filter((name, index) => !!name && name !== labels[index]);
  const label = followUp.name || labels.join(", ") || "New request";

  return (
    <label
      htmlFor={`linked-workflow-${followUp.workflowId}`}
      className={cn(
        "flex items-start gap-2.5 rounded-brand p-1.5 hover:bg-neutral-50",
        isDisabled ? "cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <Checkbox
        id={`linked-workflow-${followUp.workflowId}`}
        checked={!isSkipped}
        disabled={isDisabled}
        onCheckedChange={() => onToggle(followUp.workflowId)}
        className="mt-0.5"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        {/* Wraps rather than truncating: the sidebar is 320px wide and an
            ellipsis on "IT Equipment Request" hides the word that identifies
            it. */}
        <span
          className={cn(
            "font-inter font-medium text-sm",
            isSkipped ? "text-neutral-400 line-through" : "text-neutral-900"
          )}
        >
          {label}
        </span>
        {types.length > 0 ? (
          <span className="font-inter text-neutral-500 text-xs">
            {types.join(", ")}
          </span>
        ) : null}
      </span>
      {/* A workflow with no `run_at` files its requests the moment the approval
          lands; one with a date only schedules them, which the approver should
          not read as "created". */}
      <span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 font-inter font-semibold text-amber-800 text-xs uppercase">
        {followUp.isDeferred ? "Scheduled" : "Auto"}
      </span>
    </label>
  );
};
